"use client"

import * as React from "react"

import { DashboardEngineLabelsProvider, type DashboardEngineLabels } from "@ibada/dashboard-engine"

import { useTranslations } from "@/i18n"

/**
 * Feeds the shared dashboard engine this app's translated chrome strings.
 *
 * The engine used to call `useTranslations("dashboard.engine")` itself, which tied it to the tenant app's
 * i18n entrypoint. It now declares what text it needs and the host supplies it — so the same engine runs in
 * the admin app, which resolves locales through a different module entirely.
 *
 * The keys stay in `dashboard.engine` in each app's messages, so nothing needs re-translating.
 */
export function TenantDashboardEngineLabels({ children }: { children: React.ReactNode }) {
  const t = useTranslations("dashboard.engine")

  const labels = React.useMemo<DashboardEngineLabels>(
    () => ({
      configure: t("configure"),
      editing: t("editing"),
      addWidgets: t("addWidgets"),
      done: t("done"),
      addDescription: t("addDescription"),
      allVisible: t("allVisible"),
      allVisibleHint: t("allVisibleHint"),
      addShort: t("addShort"),
      addNamed: (name: string) => t("addNamed", { name }),
      hide: t("hide"),
      resize: t("resize"),
      dragToMove: t("dragToMove"),
      refreshing: t("refreshing"),
      actions: t("actions"),
      refresh: t("refresh"),
      viewDetails: t("viewDetails"),
      expand: t("expand"),
      contentError: t("contentError"),
      contentEmpty: t("contentEmpty"),
      spans: { small: t("spans.small"), medium: t("spans.medium"), large: t("spans.large") },
      reset: t("reset"),
    }),
    [t],
  )

  return <DashboardEngineLabelsProvider labels={labels}>{children}</DashboardEngineLabelsProvider>
}
